import { useCallback, useLayoutEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Toast } from '@/lib/Toast';
import { IUserMembership, User } from '@/types/api/users';

export type RequiredRegistrationStep =
  | 'VALIDATE_EMAIL'
  | 'VALIDATE_CPF'
  | 'REGISTER_COMPANY';

export type RegistrationSteps =
  | RequiredRegistrationStep
  | 'VALIDATE_CNPJ'
  | 'REGISTER_SHIPPING'
  | 'CONFIRM_ADDRESS'
  | 'VALIDATE_PHONE'
  | 'CHECK_PHONE'
  | 'INVITE_ADMIN'
  | 'CLOSED';

export const QUERIES = {
  verified_email: 'verified_email',
  validate_cpf: 'validate_cpf',
  configure_company: 'configure_company',
  configure_shipping: 'configure_shipping',
  validate_phone: 'validate_phone',
  validate_cnpj: 'validate_cnpj',
  confirm_address: 'confirm_address',
  invite_admin: 'invite_admin',
  check_phone: 'check_phone',
};

const QUERIES_REGISTRATION_STEPS: Record<string, RegistrationSteps> = {
  [QUERIES.verified_email]: 'VALIDATE_EMAIL',
  [QUERIES.validate_cpf]: 'VALIDATE_CPF',
  [QUERIES.configure_company]: 'REGISTER_COMPANY',
  [QUERIES.configure_shipping]: 'REGISTER_SHIPPING',
  [QUERIES.validate_phone]: 'VALIDATE_PHONE',
  [QUERIES.validate_cnpj]: 'VALIDATE_CNPJ',
  [QUERIES.confirm_address]: 'CONFIRM_ADDRESS',
  [QUERIES.invite_admin]: 'INVITE_ADMIN',
  [QUERIES.check_phone]: 'CHECK_PHONE',
};

function getNextRequiredStep(
  user: User,
  membership?: IUserMembership
): RequiredRegistrationStep | undefined {
  if (!user.verified_email) {
    return 'VALIDATE_EMAIL';
  }
  if (!user.verified_cpf) {
    return 'VALIDATE_CPF';
  }
  if (!membership?.company.has_confirmed_configuration) {
    return 'REGISTER_COMPANY';
  }
}

export const useRegistrationFlow = (
  user: User,
  membership?: IUserMembership
) => {
  const { query } = useRouter();
  const [registrationStep, setRegistrationStep] =
    useState<RegistrationSteps>('CLOSED');

  const hasRequiredSteps = getNextRequiredStep(user, membership);

  const closeRegistrationStep = () => setRegistrationStep('CLOSED');

  const ifStepAlreadyCompleted = useCallback(
    (step: RegistrationSteps): boolean => {
      const stepCompletionConditions = {
        VALIDATE_EMAIL: user.verified_email,
        VALIDATE_CPF: user.verified_cpf,
        VALIDATE_PHONE: user.verified_phone,
        VALIDATE_CNPJ: membership?.is_superadmin,
        INVITE_ADMIN: membership?.company.has_superadmins,
        CHECK_PHONE: user.verified_phone,
        REGISTER_COMPANY: membership?.company.has_confirmed_configuration,
        REGISTER_SHIPPING: membership?.company.has_confirmed_shipping,
        CONFIRM_ADDRESS: membership?.company.has_confirmed_address,
        CLOSED: false,
      };

      return stepCompletionConditions[step] ?? false;
    },
    [user, membership]
  );

  const setRegistrationStepWithValidation = (newStep: RegistrationSteps) => {
    if (hasRequiredSteps && hasRequiredSteps !== newStep) {
      setRegistrationStep(hasRequiredSteps);

      Toast('error', 'Finalize este passo obrigatório para avançar');
      return;
    }

    const newStepAlreadyCompleted = ifStepAlreadyCompleted(newStep);

    setRegistrationStep(newStepAlreadyCompleted ? 'CLOSED' : newStep);
  };

  const getRegistrationStepByQuery = useCallback((): RegistrationSteps => {
    const registrationStepQuery = Object.keys(query).find(
      key => query[key] === 'true' && QUERIES_REGISTRATION_STEPS[key]
    );
    if (!registrationStepQuery) return registrationStep;

    const newStep = QUERIES_REGISTRATION_STEPS[registrationStepQuery];
    const stepAlreadyCompleted = ifStepAlreadyCompleted(newStep);

    return stepAlreadyCompleted ? 'CLOSED' : newStep;
  }, [query, ifStepAlreadyCompleted, registrationStep]);

  useLayoutEffect(() => {
    const newStep = getRegistrationStepByQuery();

    setRegistrationStep(prevState => {
      if (newStep !== registrationStep) {
        const hasRequiredSteps = getNextRequiredStep(user, membership);

        return hasRequiredSteps === newStep || !hasRequiredSteps
          ? newStep
          : prevState;
      }

      return prevState;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return {
    registrationStep,
    closeRegistrationStep,
    setRegistrationStepWithValidation,
    hasRequiredSteps,
  };
};
